import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { getAnnouncements, getAnnouncementById } from '../../apis/Announcement.js';
import DOMPurify from 'dompurify';
import Cookies from 'js-cookie';

const ROTATE_MS = 8000;

const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
};

const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ') 
    .replace(/\s+/g, ' ')
    .trim();
};

// slide variants for one-by-one horizontal movement
const slideVariants = {
  enter: { x: '100%', opacity: 0 },
  center: {
    x: '0%',
    opacity: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  exit: {
    x: '-100%',
    opacity: 0,
    transition: { duration: 0.5, ease: 'easeIn' },
  },
};

export default function Header() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const [fallback, setFallback] = useState('');
  const timerRef = useRef(null);
  const [trialStatus, setTrialStatus] = useState(null);

  // 1) fetch announcements – let backend handle 3-day / date logic
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await getAnnouncements({ page: 1, per_page: 100 });
        const raw = Array.isArray(data?.announcements)
          ? data.announcements
          : [];

        const filtered = raw
          // ✅ only show active announcements in header
          .filter((a) => (a?.is_active ?? true) === true)
          // normalize fields for header use
          .map((a) => ({
            id: a.announcement_id,
            title: String(a.title ?? '').trim(),
            description: String(a.description ?? '').trim(),
            scheduled_datetime: a.scheduled_datetime,
          }))
          // skip completely empty items
          .filter((a) => a.title || a.description)
          // sort by scheduled date (oldest → newest)
          .sort((a, b) => {
            const A = new Date(a.scheduled_datetime || 0).getTime();
            const B = new Date(b.scheduled_datetime || 0).getTime();
            return A - B;
          });

        if (!mounted) return;

        if (filtered.length === 0) {
          setItems([]);
          setFallback('Explore our latest tool and enhancement ➡️');
        } else {
          setItems(filtered);
          setFallback('');
        }
      } catch (e) {
        console.error('[Header] announcements error:', e);
        if (!mounted) return;
        setItems([]);
        setFallback('Explore our latest tool and enhancement ➡️');
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const trialStatusStr = Cookies.get('trialStatus');
    if (trialStatusStr) {
      try {
        setTrialStatus(JSON.parse(trialStatusStr));
      } catch (e) {
        console.error('Failed to parse trial status:', e);
      }
    }
  }, []);

  // 2) rotate items (one-by-one)
  useEffect(() => {
    clearInterval(timerRef.current);
    if (items.length > 1) {
      timerRef.current = setInterval(() => {
        setIdx((i) => (i + 1) % items.length);
        setTooltipOpen(false);
      }, ROTATE_MS);
    }
    return () => clearInterval(timerRef.current);
  }, [items.length]);

  const current = items[idx];
  const titleText = current?.title || stripHtml(current?.description || '') || fallback;

  const handleClick = async () => {
    if (!current) {
      setDesc(fallback || 'No description');
      setTooltipOpen((v) => !v);
      return;
    }

    try {
      const full = await getAnnouncementById(current.id);
      setDesc(
        String(
          full?.description || current.description || 'No description',
        ).trim(),
      );
    } catch {
      setDesc(
        String(current?.description || 'No description').trim(),
      );
    }
    setTooltipOpen((v) => !v);
  };

  const content = useMemo(() => {
    const text =
      titleText || fallback || 'Explore our latest tool and enhancement ➡️';

    const descriptionHtml = desc || current?.description || 'No description';
    const sanitizedDesc = sanitizeHtml(descriptionHtml);

    return (
      <Tooltip
        arrow
        open={tooltipOpen}
        onClose={() => setTooltipOpen(false)}
        disableHoverListener
        title={
          <Box sx={{ p: 0.5, maxWidth: 480, whiteSpace: 'normal' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mb: 0.5 }}
            >
              {current?.title || 'Announcement'}
            </Typography>
            <Box
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                '& a': {
                  color: '#90caf9',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: '#64b5f6',
                  },
                },
                '& p': {
                  margin: '0.25em 0',
                },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedDesc }}
            />
          </Box>
        }
      >
        <Typography
          variant="body2"
          onClick={handleClick}
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: 13.5,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          🚀 {text}
        </Typography>
      </Tooltip>
    );
  }, [tooltipOpen, titleText, desc, current?.title, current?.description, fallback]);

  return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 36,
          zIndex: 1400,
          bgcolor: '#5965A6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          px: 2,
        }}
      >
        {trialStatus && trialStatus.is_on_trial && !trialStatus.is_paid && (
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: 'rgba(255, 193, 7, 0.15)',
              borderLeft: '3px solid #ffc107',
              borderRadius: '4px',
              fontSize: '0.75rem',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#fff',
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            >
              Trial: {trialStatus.days_remaining !== null && trialStatus.days_remaining !== undefined
                ? `${trialStatus.days_remaining} days left`
                : 'Active'}
            </Typography>
          </Box>
        )}
        {/* viewport to mask slide motion */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 920,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current?.id || `fallback-${titleText}`}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {content}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
}
